import axios from 'axios';

// Token management
let accessToken = null;
let refreshFunction = null;

export const setAccessToken = (token) => {
  accessToken = token;
};

export const getAccessToken = () => accessToken;

// Allow AuthContext to register its refresh function
export const setRefreshFunction = (refreshFn) => {
  refreshFunction = refreshFn;
};

// CSRF token management
let csrfToken = null;
let csrfTokenPromise = null;

export const initializeCsrf = async () => {
  if (csrfToken) return csrfToken;
  
  try {
    const response = await instance.get('/auth/csrf-token');
    csrfToken = response.data.csrfToken;
    return csrfToken;
  } catch (err) {
    console.error('Failed to fetch CSRF token:', err);
    csrfToken = null;
    throw err;
  }
};

// Axios instance configuration
const normalizeApiBaseUrl = (rawBase) => {
  if (!rawBase) return '';
  const trimmed = String(rawBase).replace(/\/+$/, '');
  return trimmed.endsWith('/api') ? trimmed : `${trimmed}/api`;
};

const inferredProdApiBaseUrl = (() => {
  if (typeof window === 'undefined') return '';
  const origin = window.location?.origin;
  return origin ? `${origin}/api` : '';
})();

const fallbackBaseUrl =
  process.env.NODE_ENV === 'production'
    ? inferredProdApiBaseUrl
    : 'http://localhost:5002/api';

const instance = axios.create({
  baseURL: process.env.REACT_APP_API_URL ? normalizeApiBaseUrl(process.env.REACT_APP_API_URL) : fallbackBaseUrl,
  withCredentials: true,
  timeout: 30000,
});

// App version for debugging
const APP_VERSION = process.env.REACT_APP_APP_VERSION || process.env.REACT_APP_VERSION || 'dev';

// Initialize CSRF token on startup (non-blocking)
csrfTokenPromise = initializeCsrf().catch(() => null);

// Request interceptor
instance.interceptors.request.use(
  async (config) => {
    console.log('[axios] request:', config.method?.toUpperCase(), config.url);
    // Add CSRF token to mutating requests
    if (['post', 'put', 'patch', 'delete'].includes(config.method?.toLowerCase())) {
      try {
        if (!csrfToken) {
          await csrfTokenPromise;
          if (!csrfToken) await initializeCsrf();
        }
        if (csrfToken) {
          config.headers['X-CSRF-Token'] = csrfToken;
        }
      } catch (error) {
        console.warn('Failed to attach CSRF token:', error);
      }
    }

    // Define public endpoints that don't require authentication
    const publicEndpoints = [
      '/auth/login',
      '/auth/signup',
      '/auth/resend-verification',
      '/auth/verify-email',
      '/auth/forgot-password',
      '/auth/reset-password',
      '/auth/csrf-token',
      '/auth/refresh', // treat refresh as public: it authenticates via cookie
    ];
    
    const isPublic = publicEndpoints.some(endpoint => config.url?.includes(endpoint));

    // Add Bearer token for protected endpoints
    if (!isPublic && accessToken) {
      console.log('[axios] Attaching access token snippet to request');
      config.headers.Authorization = `Bearer ${accessToken}`;
    }

    // Add app version header for debugging
    if (APP_VERSION) {
      config.headers['X-App-Version'] = APP_VERSION;
    }

    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor - Handle token refresh properly
instance.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    console.warn('[axios] response error for', originalRequest?.url, 'status:', error.response?.status);
    const apiErrorCode = error.response?.data?.code;

    // Handle CSRF token errors
    const isCsrfError = error.response?.status === 403 && 
      (error.response?.data?.error?.includes('CSRF') ||
       error.response?.data?.code === 'EBADCSRFTOKEN');

    if (isCsrfError && !originalRequest._retryCsrf) {
      originalRequest._retryCsrf = true;
      try {
        csrfToken = null;
        await initializeCsrf();
        if (csrfToken) {
          originalRequest.headers['X-CSRF-Token'] = csrfToken;
          return instance(originalRequest);
        }
      } catch (csrfError) {
        console.warn('CSRF token refresh failed:', csrfError);
      }
    }

    // Handle 401 Unauthorized - Delegate to AuthContext
    // NOTE: we purposely do NOT require `accessToken` to be present here. In dev HMR reloads
    // the in-memory token can be lost; we should attempt a refresh (via refreshFunction)
    // so the app can rehydrate the session silently.
    // Also serialize refresh attempts locally so concurrent 401s wait for a single refresh.
  const status = error.response?.status;
  if (status === 401 &&
    apiErrorCode !== 'location_letter_provider_not_configured' &&
    !originalRequest.url?.includes('/auth/refresh') && // Don't retry refresh endpoint
    !originalRequest.url?.includes('/auth/login') && // Don't retry login endpoint
    !originalRequest.url?.includes('/auth/logout') && // Don't retry logout endpoint (prevents loops)
    !originalRequest._retryAuth && // Don't retry more than once
    refreshFunction) { // Only if AuthContext has registered a refresh function

      originalRequest._retryAuth = true;

      // Local refresh queue/lock
      if (!instance._isRefreshing) instance._isRefreshing = false;
      if (!instance._refreshSubscribers) instance._refreshSubscribers = [];

      const subscribeToRefresh = () => new Promise((resolve, reject) => {
        instance._refreshSubscribers.push({ resolve, reject });
      });

      const notifyRefreshSuccess = () => {
        instance._refreshSubscribers.forEach(s => s.resolve());
        instance._refreshSubscribers = [];
      };

      const notifyRefreshFailure = (err) => {
        instance._refreshSubscribers.forEach(s => s.reject(err));
        instance._refreshSubscribers = [];
      };

      try {
        if (instance._isRefreshing) {
          // Wait for the ongoing refresh to finish
          await subscribeToRefresh();
          const token = getAccessToken();
          if (token) {
            originalRequest.headers.Authorization = `Bearer ${token}`;
            return instance(originalRequest);
          }
          // No token after wait - let the error fall through
          return Promise.reject(error);
        }

        // Start refresh
        instance._isRefreshing = true;
        console.log('[axios] Token expired, attempting refresh via AuthContext...');
  const newToken = await refreshFunction();
        console.log('[axios] refreshFunction returned:', !!newToken);

        instance._isRefreshing = false;

        if (newToken) {
          // Notify any waiters
          notifyRefreshSuccess();
          // Retry the original request with new token
          originalRequest.headers.Authorization = `Bearer ${newToken}`;
          console.log('Token refreshed successfully, retrying original request');
          return instance(originalRequest);
        }

        console.warn('[axios] refreshFunction returned no token');
        notifyRefreshFailure(new Error('Refresh returned no token'));
        return Promise.reject(error);

      } catch (refreshError) {
        console.warn('Token refresh failed:', refreshError);
        instance._isRefreshing = false;
        notifyRefreshFailure(refreshError);
        // Let AuthContext handle clearing tokens / redirecting. Bubble original error.
        return Promise.reject(error);
      }
    }

    return Promise.reject(error);
  }
);

export default instance;